import { useQuery } from '@apollo/client';
import { GET_CASE_ID_FIELD_VISIBILITY } from '../../../graphql';

export type CaseIdFields = {
  householdIdEnabled: boolean;
  householdCodeEnabled: boolean;
  preset: string;
  mode: string;
  /** The system allocates Case IDs. When false the organisation captures them by hand. */
  isAutomatic: boolean;
  /** Household-linked formats allow a person to have no household at all, giving them a standalone ID. */
  supportsNoHousehold: boolean;
  loading: boolean;
};

/**
 * Which Case ID fields this organisation uses, for the screens that render them. Defaults to all
 * three while loading and for an organisation that has not configured anything, which is the legacy
 * behaviour and keeps the existing organisation unchanged.
 */
export const useCaseIdFields = (): CaseIdFields => {
  // cache-and-network, not cache-first: an administrator can change the allocation mode at any time,
  // and with cache-first every screen kept serving the value from the first fetch until a full page
  // reload. The cached value still renders immediately, so this costs a background request, not a
  // spinner.
  const { data, loading } = useQuery(GET_CASE_ID_FIELD_VISIBILITY, { fetchPolicy: 'cache-and-network' });

  const visibility = data?.visibility;
  const householdIdEnabled = visibility ? visibility.householdIdEnabled !== false : true;

  const mode = visibility?.mode || 'AUTOMATIC';

  return {
    householdIdEnabled,
    householdCodeEnabled: visibility ? householdIdEnabled && visibility.householdCodeEnabled !== false : true,
    preset: visibility?.preset || 'LEGACY_HOUSEHOLD_CODE',
    mode,
    isAutomatic: mode !== 'MANUAL',
    supportsNoHousehold: visibility?.preset === 'HOUSEHOLD_LINKED_SERVICE_RECIPIENT',
    loading,
  };
};
